import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import http from '../Api/http';
import Util from '../Util';
import { Button, Input } from "@mui/material";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, Clock } from "lucide-react";

const ContactUs = () => {
    const { t, i18n } = useTranslation();
    const language = i18n.language;
    const isRTL = language === "ar" || language === "he";

    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [errors, setErrors] = useState({});
    const [successMessage, setSuccessMessage] = useState("");
    const [failedMessage, setFailedMessage] = useState("");
    const [formData, setFormData] = useState({
        Id: 0,
        InsDate: new Date(),
        Name: '',
        Email: '',
        Phone: '',
        Notes: '',
    });


    useEffect(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });  
    }, []);

    const workingHours = [
        { key: "sunThu", value: '08:00 - 13:30' },
        { key: "fri", value: '08:00 - 12:30' },
        { key: "sat", value: 'closed' },
    ]

    const handleChange = (e) => {
        const { name, value } = e.target;
        setErrors(prev => ({ ...prev, [name]: '' }));
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const validate = () => {
        let newErrors = {};
        if (!formData.Name) newErrors.Name = t('requiredField');
        if (!formData.Phone) newErrors.Phone = t('requiredField');
        if (!formData.Notes) newErrors.Notes = t('requiredField');
        if (formData) {
            if (formData.Phone) {
                if (!Util.checkPhone(formData.Phone, false)) {
                    newErrors.Phone = t('phoneError')
                }
            } if (formData.Email) {
                if (!Util.checkEmail(formData.Email)) {
                    newErrors.Email = t('emailError')
                }
            }
        }
        return newErrors;
    };

    const onSave = async (e) => {
        const newErrors = validate();
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        try {
            setLoading(true);
            setSubmitted(true);

            const result = await http.postJsonAxios(http.actions.ContactUs.Save, formData, t);
            if (result.status === 200 && result.data.success) {
                setErrors({});
                setFormData({
                    Id: 0,
                    InsDate: new Date(),
                    Name: '',
                    Email: '',
                    Phone: '',
                    Notes: '',
                });
                setSuccessMessage(t('done'));
                setTimeout(() => setSuccessMessage(""), 3000); // إخفاء الرسالة بعد 3 ثواني

            } else {
                setFailedMessage(result.data.errorText || t('failed'));
                setTimeout(() => setFailedMessage(""), 3000);
            }
        }
        catch (e) {

        }
        finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen pt-10 pb-20 bg-background" dir={t('dir')}>
            <div className="container px-4 mx-auto">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-center mb-16"
                >
                    <h1 className="text-4xl md:text-5xl font-bold mb-6 text-slate-900 dark:text-white">
                        {t('contactUs')}
                    </h1>
                    <div className="h-1.5 w-24 bg-accent mx-auto rounded-full" />
                    <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                        {t('contactHelp')}
                    </p>
                </motion.div>

                <div className="grid lg:grid-cols-2 gap-6 max-w-6xl mx-auto">
                    {/* Info Section */}
                    <div className="space-y-6">
                        <div className="p-8 border-none shadow-lg bg-primary text-white">
                            <h3 className="text-2xl font-bold mb-6">
                                {t('contactInfo')}
                            </h3>

                            <div className="space-y-6">
                                <div className="flex items-start gap-4">
                                    <MapPin className="w-6 h-6 shrink-0 text-accent" />
                                    <div>
                                        <h4 className="font-bold mb-1">{t('address')}</h4>
                                        <p className="text-white/80">{t('coAddress')}</p>
                                    </div>
                                </div>

                                <div className="flex items-start gap-4">
                                    <Phone className="w-6 h-6 shrink-0 text-accent" />
                                    <div>
                                        <h4 className="font-bold mb-1">{t('phone')}</h4>
                                        <p className="text-white/80" dir="ltr">+972 59-984-4222</p>
                                        <p className="text-white/80" dir="ltr">+972 50-544-5766</p>
                                    </div>
                                </div>

                                <div className="flex items-start gap-4">
                                    <Mail className="w-6 h-6 shrink-0 text-accent" />
                                    <div>
                                        <h4 className="font-bold mb-1">{t('email')}</h4>
                                        <p className="text-white/80">info@abdobus.com</p>
                                        {/* <p className="text-white/80">info@abdobus.com</p> */}
                                    </div>
                                </div>

                                <div className="flex items-start gap-4">
                                    <Clock className="w-6 h-6 shrink-0 text-accent" />
                                    <div>
                                        <h4 className="font-bold mb-1">{t('workingHours')}</h4>
                                        {/* <p className="text-white/80">Sun - Thu: 08:00 - 17:00</p>
                                        <p className="text-white/80">Fri - Sat: 08:00 - 15:30</p> */}
                                        {
                                            workingHours.map((element, index) => (
                                                <div
                                                    key={index}
                                                    className="flex justify-between items-center py-2 last:border-0"
                                                >
                                                    <span>{t(element.key)}&nbsp;</span>
                                                    <span>{element.value === "closed" ? t(element.value) : element.value}</span>
                                                </div>
                                            ))
                                        }
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Map */}
                        {/* <div className="h-64 rounded-3xl overflow-hidden shadow-lg border border-border">
                            <img
                                src="https://images.unsplash.com/photo-1524661135-423995f22d0b?auto=format&fit=crop&q=80&w=800"
                                alt="Map Location"
                                className="w-full h-full object-cover"
                            />
                        </div> */}
                    </div>

                    {/* Form Section */}
                    <div className="p-8 shadow-xl rounded-2xl bg-white border border-slate-200">
                        <h3 className="text-2xl font-bold mb-8 text-slate-900">
                            {t('sendAMessage')}
                        </h3>

                        <div className="space-y-6">
                            <div className="flex flex-col gap-2">
                                <label className="text-sm font-medium">
                                    {t('name')}
                                </label>
                                <input
                                    name="Name"
                                    value={formData.Name}
                                    onChange={handleChange}
                                    className="w-full mb-1 bg-slate-50 rounded-lg border px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none resize-none"
                                />
                                {errors.Name && (<p className="text-red-500 text-sm">{errors.Name}</p>)}
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="flex flex-col gap-2">
                                    <label className="text-sm font-medium">
                                        {t('email')}
                                    </label>
                                    <input
                                        name="Email"
                                        type="email"
                                        value={formData.Email}
                                        onChange={handleChange}
                                        className="w-full mb-1 bg-slate-50 rounded-lg border px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none resize-none"
                                    />
                                    {errors.Email && (<p className="text-red-500 text-sm">{errors.Email}</p>)}
                                </div>
                                <div className="flex flex-col gap-2">
                                    <label className="text-sm font-medium">
                                        {t('phone')}
                                    </label>
                                    <input
                                        name="Phone"
                                        value={formData.Phone}
                                        onChange={handleChange}
                                        className="w-full mb-1 bg-slate-50 rounded-lg border px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none resize-none"
                                    />
                                    {errors.Phone && (<p className="text-red-500 text-sm">{errors.Phone}</p>)}
                                </div>
                            </div>
                            <div className="flex flex-col gap-2">
                                <label className="text-sm font-medium">
                                    {t('yourMessage')}
                                </label>
                                <textarea
                                    name="Notes"
                                    value={formData.Notes}
                                    onChange={handleChange}
                                    className="w-full mb-1 bg-slate-50 rounded-lg border px-3 py-3 focus:ring-2 focus:ring-blue-500 outline-none resize-none"
                                />
                                {errors.Notes && (<p className="text-red-500 text-sm">{errors.Notes}</p>)}
                            </div>

                            <button
                                onClick={() => { setSubmitted(true); onSave(); }}
                                disabled={loading}
                                className="w-full h-10 rounded-lg bg-blue-600 text-lg font-bold text-white hover:bg-blue-700 transition"
                            >
                                {t('sendMessage')}
                            </button>
                            {successMessage && (
                                <div className="bg-green-100 text-green-800 p-2 rounded mb-2 text-center border border-green-400">
                                    {successMessage}
                                </div>
                            )}
                            {failedMessage && (
                                <div className="bg-red-100 text-red-800 p-2 rounded mb-2 text-center border border-red-400">
                                    {failedMessage}
                                </div>
                            )}
                        </div>
                    </div>

                </div>
            </div>
        </div >
    );
};

export default ContactUs;
